import React, {useEffect, useMemo, useState} from 'react';
import {Grid, FormControl, InputLabel, Select, MenuItem, Typography} from '@material-ui/core';
import SelectRS, {components as rsComponents} from 'react-select';
import {findFlagUrlByIso2Code} from 'country-flags-svg';
import {useTheme, makeStyles} from '@material-ui/core/styles';
import useStore from '../../../../store/store';
import usePersistentStore from '../../../../store/persistentStore';
import usePersistentAuthStore from '../../../../store/persistentAuthStore';
import {APICall} from '../../../../api/api';
import {getSSPLogo} from '../../../helpers/getSSPLogo';
import {toAnalyticsPeriod} from '../helpers/dataHelpers';
import millify from 'millify';
import countriesList from '../../../misc/countries.json';

const useStyles = makeStyles(theme => ({
  container: {
    border: `1px solid ${theme.palette.divider}`,
    padding: 16,
    position: 'relative',
    borderRadius: 4,
  },
  label: {
    position: 'absolute',
    top: -10,
    left: 14,
    backgroundColor: theme.palette.background.default,
    paddingLeft: 6,
    paddingRight: 6,
    fontSize: '0.8rem',
  },
  optionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  flagImg: {
    width: 20,
    height: 14,
    objectFit: 'cover',
  },
  logoImg: {
    width: 16,
    height: 16,
    objectFit: 'contain',
    borderRadius: 2,
  },
  optionTotal: {
    marginLeft: 'auto',
    color: theme.palette.text.secondary,
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  singleValue: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    minWidth: 0,
    flex: 1,
    justifyContent: 'space-between',
  },
  singleValueLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
  },
  singleValueTotal: {
    color: theme.palette.text.secondary,
    textAlign: 'right',
    whiteSpace: 'nowrap',
    marginLeft: 8,
    flexShrink: 0,
    overflow: 'visible',
    right: 10,
    position: 'relative',
  },
}));

const AnalyticsFilters = ({onMenuOpen, onMenuClose}) => {
  const analyticsCountry = useStore(state => state.analyticsCountryFilter);
  const analyticsDevice = useStore(state => state.analyticsDeviceFilter);
  const analyticsBidder = useStore(state => state.analyticsBidderFilter);
  const analyticsAttributeGroups = useStore(state => state.analyticsAttributeGroups);
  const selection = useStore(state => state.selection);
  const period = useStore(state => state.period);
  const realtimeMinutes = usePersistentStore(state => state.realtimeMinutes);
  const setState = useStore(state => state.setState);
  const theme = useTheme();
  const classes = useStyles();
  const {auth} = usePersistentAuthStore();
  const [options, setOptions] = useState({country: [], device: [], bidder: []});
  const [loading, setLoading] = useState({country: false, device: false, bidder: false});
  const handleMenuOpen = () => {
    setState('disableTypingForAccountSelector', true);
    setState('analyticsFiltersOpen', true);
    onMenuOpen?.();
  };
  const handleMenuClose = () => {
    setState('disableTypingForAccountSelector', false);
    setState('analyticsFiltersOpen', false);
    onMenuClose?.();
  };

  const countryLabelMap = useMemo(
    () =>
      countriesList.reduce((acc, c) => {
        acc[c.id] = {label: c.text, flagUrl: findFlagUrlByIso2Code(c.id)};
        return acc;
      }, {}),
    []
  );
  const deviceLabelMap = useMemo(
    () => ({
      0: 'Desktop',
      1: 'Tablet',
      2: 'Mobile',
    }),
    []
  );

  const customStyles = useMemo(
    () => ({
      container: base => ({...base, minWidth: 200, width: '100%'}),
      control: base => ({
        ...base,
        minHeight: 38,
        background: theme.palette.background.paper,
        borderColor: theme.palette.divider,
        boxShadow: 'none',
        '&:hover': {borderColor: theme.palette.text.secondary},
      }),
      input: base => ({...base, color: theme.palette.text.primary}),
      valueContainer: base => ({
        ...base,
        paddingLeft: 8,
        color: theme.palette.text.primary,
        display: 'flex',
        width: '100%',
        minWidth: 0,
      }),
      placeholder: base => ({...base, color: theme.palette.text.secondary}),
      singleValue: base => ({
        ...base,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        color: theme.palette.text.primary,
        width: '100%',
        minWidth: 0,
        maxWidth: 'none',
        overflow: 'visible',
      }),
      option: (base, state) => ({
        ...base,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        backgroundColor: state.isFocused
          ? theme.palette.action.hover
          : theme.palette.background.paper,
        color: theme.palette.text.primary,
        cursor: 'pointer',
      }),
      menu: base => ({
        ...base,
        zIndex: theme.zIndex.modal,
        background: theme.palette.background.paper,
        boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
      }),
      menuPortal: base => ({...base, zIndex: theme.zIndex.modal}),
    }),
    [theme]
  );

  const countryStyles = useMemo(
    () => ({
      ...customStyles,
      container: base => {
        const merged = customStyles.container ? customStyles.container(base) : {...base};
        return {...merged, minWidth: '90%', width: '97%'};
      },
    }),
    [customStyles]
  );

  const Option = props => {
    const {data} = props;
    const total = data.total;
    return (
      <rsComponents.Option {...props}>
        <span className={classes.optionRow}>
          {data.logo && !data.flagUrl && (
            <img alt={data.value} src={data.logo} className={classes.logoImg} />
          )}
          {data.flagUrl && <img alt={data.value} src={data.flagUrl} className={classes.flagImg} />}
          {data.label}
        </span>
        {total != null && <span className={classes.optionTotal}>{millify(total)}</span>}
      </rsComponents.Option>
    );
  };

  const SingleValue = props => {
    const {data} = props;
    const total = data.total;
    return (
      <rsComponents.SingleValue {...props}>
        <span className={classes.singleValue}>
          <span className={classes.singleValueLeft}>
            {data.logo && !data.flagUrl && (
              <img alt={data.value} src={data.logo} className={classes.logoImg} />
            )}
            {data.flagUrl && (
              <img alt={data.value} src={data.flagUrl} className={classes.flagImg} />
            )}
            {data.label}
          </span>
          {total != null && (
            <span className={classes.singleValueTotal}>{millify(total)} impressions</span>
          )}
        </span>
      </rsComponents.SingleValue>
    );
  };

  // Fetch totals for analytics filters from Kusto and log responses
  useEffect(() => {
    if (!auth?.token || !selection || !period) return;

    const baseBody = {
      selection,
      period: toAnalyticsPeriod(period, realtimeMinutes),
    };

    const makeRequest = async filter => {
      try {
        setLoading(prev => ({...prev, [filter]: true}));
        const response = await APICall({
          endpoint: 'getAzureTotalsForFilter',
          options: {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${auth.token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              ...baseBody,
              filter,
            }),
          },
        });

        if (response?.error || response?.success === false) {
          throw new Error(response?.error || 'Failed to fetch filter totals.');
        }
        if (response?.success && Array.isArray(response.data)) {
          const sorted = [...response.data].sort(
            (a, b) => (b.TotalBidResponses || 0) - (a.TotalBidResponses || 0)
          );

          const totalSum = sorted.reduce((sum, item) => sum + (item.TotalBidResponses || 0), 0);

          const mappedOptions =
            filter === 'country'
              ? sorted.map(item => {
                  const key = item.country;
                  const labelInfo = countryLabelMap[key] || {label: key, flagUrl: null};
                  return {
                    value: key,
                    label: labelInfo.label,
                    flagUrl: labelInfo.flagUrl,
                    total: item.TotalBidResponses,
                    type: 'country',
                  };
                })
              : filter === 'device'
                ? sorted.map(item => {
                    const key = String(item.device);
                    return {
                      value: key,
                      label: deviceLabelMap[key] || key,
                      total: item.TotalBidResponses,
                      type: 'device',
                    };
                  })
                : sorted.map(item => {
                    const key = item.bidder;
                    return {
                      value: key,
                      label: key,
                      logo: getSSPLogo(key || '', analyticsAttributeGroups),
                      total: item.TotalBidResponses,
                      type: 'bidder',
                    };
                  });

          const allOption = {
            value: null,
            label:
              filter === 'country'
                ? 'All countries'
                : filter === 'device'
                  ? 'All devices'
                  : 'All bidders',
            total: totalSum,
            type: filter,
          };

          const withAll = [allOption, ...mappedOptions];

          setOptions(prev => ({...prev, [filter]: withAll}));
        }
      } catch (error) {
        console.error(`Error fetching Azure totals for ${filter}:`, error);
      } finally {
        setLoading(prev => ({...prev, [filter]: false}));
      }
    };

    makeRequest('country');
    makeRequest('device');
    makeRequest('bidder');
  }, [auth?.token, selection, period, realtimeMinutes, analyticsAttributeGroups]);

  return (
    <Grid container wrap="wrap" alignItems="center" className={classes.container}>
      <Grid item xs={12}>
        <Typography gutterBottom color="textSecondary" className={classes.label}>
          Filters
        </Typography>
      </Grid>
      <Grid item xs={12} sm={4} md={4}>
        <SelectRS
          placeholder="All countries"
          isClearable
          options={options.country}
          value={
            analyticsCountry === null
              ? options.country.find(opt => opt.value === null) || null
              : options.country.find(opt => opt.value === analyticsCountry) || null
          }
          onChange={opt => setState('analyticsCountryFilter', opt ? opt.value : null)}
          styles={countryStyles}
          components={{Option, SingleValue}}
          menuPortalTarget={document.body}
          isLoading={loading.country}
          onMenuOpen={handleMenuOpen}
          onMenuClose={handleMenuClose}
        />
      </Grid>
      <Grid item xs={12} sm={4} md={4}>
        <SelectRS
          placeholder="All devices"
          options={options.device}
          isClearable
          value={
            analyticsDevice === null
              ? options.device.find(opt => opt.value === null) || null
              : options.device.find(opt => opt.value === analyticsDevice) || null
          }
          onChange={opt => setState('analyticsDeviceFilter', opt?.value || null)}
          styles={countryStyles}
          menuPortalTarget={document.body}
          isLoading={loading.device}
          components={{Option, SingleValue}}
          onMenuOpen={handleMenuOpen}
          onMenuClose={handleMenuClose}
        />
      </Grid>
      <Grid item xs={12} sm={4} md={4}>
        <SelectRS
          placeholder="All bidders"
          options={options.bidder}
          isClearable
          value={
            analyticsBidder === null
              ? options.bidder.find(opt => opt.value === null) || null
              : options.bidder.find(opt => opt.value === analyticsBidder) || null
          }
          onChange={opt => setState('analyticsBidderFilter', opt?.value || null)}
          styles={countryStyles}
          menuPortalTarget={document.body}
          isLoading={loading.bidder}
          components={{Option, SingleValue}}
          onMenuOpen={handleMenuOpen}
          onMenuClose={handleMenuClose}
        />
      </Grid>
    </Grid>
  );
};

export default AnalyticsFilters;
